import { faCoin, faPlus } from "@fortawesome/pro-regular-svg-icons";

import { ChangeEvent, FunctionComponent, useCallback, useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { zodResolver } from "@hookform/resolvers/zod";

import { useForm } from "react-hook-form";

import trashIcon from "@/assets/icons/trashIcon.svg?url";

import { Icon, Modal } from "@/components";
import { InputField } from "@/components/InputField/InputField";
import { ButtonSize } from "@/components/Button/types";
import { useAddColumnToTrackerMutation } from "@/pages/Private/redux/profileTracker/profileTracker.api";

import { Button } from "@/components/Button/Button";

import { AiApplyType, CustomColumnFrequency, CustomColumnTypes } from "@/enum/list.enum";
import { ERROR_TYPE, getAllErrors, renderErrorMessages } from "@/utils";
import { Radio } from "@/components/Radio/Radio";
import { Dropdown, AutoCompleteItem } from "@/components/Dropdown/Dropdown";
import { PermissionRoles } from "@/enum";
import { useHasPermissions } from "@/utils/useHasPermissions";
import { ColumnConfig } from "@/pages/Private/pages/List/schema/columnConfig";

import { useAppSelector } from "@/redux/hooks";
import { profileTrackerSelector } from "@/pages/Private/redux/profileTracker/profileTracker.slice";
import { getCurrentPrices } from "@/pages/Private/pages/List/utils/currentPrices";

import { CustomColumn, CustomColumnSchema } from "../../../schema/trackerColumn";
import { Tracker } from "../../../schema/profileTracker";

export interface DepartmentModalProps {
	setShowConfirm: (value: boolean) => void;
	open: boolean;
	trackerId: number;
	setOpen: (value: boolean) => void;
	columnId?: number;
	column?: ColumnConfig;
	customColumnName?: string;
	selectedTrackerOrganizationIds?: number[];
	tracker?: Tracker;
}

export const DepartmentModal: FunctionComponent<DepartmentModalProps> = ({
	setShowConfirm,
	trackerId,
	open,
	setOpen,
	columnId,
	column,
	customColumnName,
	selectedTrackerOrganizationIds,
	tracker,
}) => {
	const { records, recordsMeta } = useAppSelector(profileTrackerSelector);

	const [randomValues, setRandomValues] = useState<string[]>(
		column?.config ? (column.config as string[]) : []
	);
	const [randomValue, setRandomValue] = useState<string>();
	const { hasPermissions } = useHasPermissions();
	const [updateOrOnce, setUpdateOrOnce] = useState<boolean>(false);
	const { t } = useTranslation();
	const ts = useCallback((key: string) => t(`aiColumn.${key}`), [t]);
	const [applyTo, setApplyTo] = useState<AiApplyType>(AiApplyType.NONE);

	const [update, { isLoading, error }] = useAddColumnToTrackerMutation();

	const {
		handleSubmit,
		getValues,
		setValue,
		formState: { errors },
	} = useForm<CustomColumn>({
		defaultValues: {
			customColumnType: CustomColumnTypes.DEPARTMENT_SIZE,
			...column,
			customColumnName: customColumnName || column?.name,
		},
		resolver: zodResolver(CustomColumnSchema),
	});

	useEffect(() => {
		if (column) {
			if (column.updateOrOnce) {
				setUpdateOrOnce(column.updateOrOnce);
			}
		}
	}, [column]);

	const trackerOrganizationIds =
		applyTo === AiApplyType.FIRST_10
			? records.slice(0, 10).map((p) => p.id)
			: applyTo === AiApplyType.CURRENTLY_SELECTED
				? selectedTrackerOrganizationIds
				: undefined;

	const onSubmit = async (values: CustomColumn) => {
		try {
			values.value = randomValues;

			await update({
				id: trackerId,
				updateOrOnce,
				columnId,
				trackerOrganizationIds,
				...values,
			}).unwrap();

			setOpen(false);
		} catch (err) {
			console.error(err);
		}
	};

	const formErrors = Object.values(errors).map((error) => error?.message) as ERROR_TYPE[];

	const currentCreditCost = useMemo(() => {
		return getCurrentPrices({
			applyTo,
			meta: recordsMeta,
			singlePrice: 1,
			selectedProspectIds: selectedTrackerOrganizationIds,
			tracker: tracker,
		});
	}, [applyTo, getValues("online"), recordsMeta.itemCount]);

	return (
		<Modal
			disableSave={!randomValues.length}
			handleClose={() => {
				setShowConfirm(true);
			}}
			handleSave={handleSubmit(onSubmit)}
			isLoading={isLoading}
			isOpened={open}
			size="md"
			submitButtonText={t("basics.confirm")}
			title="Create department size column"
		>
			<div className="text-ssm font-medium mb-4">
				<InputField
					error={!!errors.customColumnName}
					handleChange={function (event: ChangeEvent<HTMLInputElement>): void {
						setValue("customColumnName", event.target.value, {
							shouldValidate: true,
							shouldDirty: true,
						});
					}}
					label="Column Name"
					name={"name"}
					placeholder="Enter column name"
					value={getValues("customColumnName")}
				/>
			</div>

			<div className="flex flex-col">
				<div className="flex flex-row items-start space-y-4 -mb-2">
					<InputField
						className="!mb-0 grow mr-4"
						error={!!errors.value}
						handleChange={function (event: ChangeEvent<HTMLInputElement>): void {
							setRandomValue(event.target.value);
						}}
						name={"randomValue"}
						placeholder="Enter job title... (e.g. Software Engineer, Business Development)"
						value={randomValue ?? ""}
						onKeyDown={(e) => {
							if (e.key === "Enter" && randomValues.length < 10) {
								if (randomValue) {
									setRandomValues([randomValue, ...(randomValues as string[])]);
								}

								setRandomValue("");
							}
						}}
					/>
					<div className="w-[140px] !mt-0 ml-4 flex items-start">
						<Button
							className="!mt-0"
							disabled={!randomValue || randomValues.length >= 10}
							image={<Icon className="mr-2" icon={faPlus} />}
							size={ButtonSize.L}
							title="Add value"
							onClick={() => {
								setRandomValues([...(randomValues as string[]), randomValue ?? ""]);
								setRandomValue("");
							}}
						/>
					</div>
				</div>
				<div className="flex flex-col text-xs text-text-meta">Maximum 10 job titles</div>
			</div>
			<div>
				{randomValues.length > 0 && (
					<div className="text-ssm font-medium mt-4 mb-2">
						List of job titles to define department:
					</div>
				)}
				{(randomValues as string[]).map((value, index) => (
					<div
						key={index}
						className="flex items-center justify-between h-[54px] px-2.5 py-4.5 rounded-xl border border-gray-200 mb-4"
					>
						<div>{value}</div>
						<button
							className="cursor-pointer"
							onClick={() => {
								const values = [...(randomValues as string[])];

								values.splice(index, 1);
								setRandomValues(values);
							}}
						>
							<img alt="trash" src={trashIcon} />
						</button>
					</div>
				))}
			</div>
			<div className="flex flex-col mt-5 border-t pt-5 border-gray-200">
				<div className="flex  gap-4 flex-col text-gray-700 items-center">
					<Dropdown
						data={[
							{
								title: ts(CustomColumnFrequency.ONCE),
								id: CustomColumnFrequency.ONCE,
							},
							{
								title: ts(CustomColumnFrequency.WEEKLY),
								id: CustomColumnFrequency.WEEKLY,
							},
							{
								title: ts(CustomColumnFrequency.MONTHLY),
								id: CustomColumnFrequency.MONTHLY,
							},
							{
								title: ts(CustomColumnFrequency.QUATERLY),
								id: CustomColumnFrequency.QUATERLY,
							},
							{
								title: ts(CustomColumnFrequency.HALFYEARLY),
								id: CustomColumnFrequency.HALFYEARLY,
							},
							{
								title: ts(CustomColumnFrequency.YEARLY),
								id: CustomColumnFrequency.YEARLY,
							},
						]}
						defaultValue={
							getValues("frequency") && {
								title: ts(getValues("frequency") as CustomColumnFrequency),
								id: getValues("frequency") as CustomColumnFrequency,
							}
						}
						error={!!errors.frequency?.message}
						floating={true}
						handleSelect={function (value?: AutoCompleteItem) {
							if (value?.id) {
								setValue("frequency", value.id as CustomColumnFrequency);
							}
						}}
						label="Execution frequency"
					/>
				</div>
				<div className="flex flex-col grow mr-2">
					<div className="text-ssm font-medium mt-4 mb-2">Update frequency</div>
					<div className="flex flex-col gap-4">
						<Radio
							className="whitespace-nowrap"
							isChecked={updateOrOnce}
							onChange={() => {
								setUpdateOrOnce(!updateOrOnce);
							}}
						>
							{ts("updateOnEachExecution")}
						</Radio>

						<Radio
							className="whitespace-nowrap"
							isChecked={!updateOrOnce}
							onChange={() => {
								setUpdateOrOnce(!updateOrOnce);
							}}
						>
							{ts("onceAfterFindingAValue")}
						</Radio>
					</div>
				</div>
			</div>
			<div className="flex flex-row mt-5 border-t pt-5 border-gray-200">
				<Dropdown
					data={[
						{
							title: ts(AiApplyType.NONE),
							id: AiApplyType.NONE,
						},
						{
							title: ts(AiApplyType.FIRST_10),
							id: AiApplyType.FIRST_10,
						},
						{
							title: ts(AiApplyType.CURRENTLY_FILTERED),
							id: AiApplyType.CURRENTLY_FILTERED,
						},
						{
							title: ts(AiApplyType.CURRENTLY_SELECTED),
							id: AiApplyType.CURRENTLY_SELECTED,
						},
						{
							title: ts(AiApplyType.ALL),
							id: AiApplyType.ALL,
						},
						{
							title: ts(AiApplyType.ALL_INCLUDING_ASSIGNED),
							id: AiApplyType.ALL_INCLUDING_ASSIGNED,
						},
					]}
					floating={false}
					handleSelect={function (value?: AutoCompleteItem) {
						if (value?.id) {
							setApplyTo(value.id as AiApplyType);
						}
					}}
					label="Apply to ..."
					value={applyTo ? { title: ts(applyTo), id: applyTo } : undefined}
				/>
			</div>
			{hasPermissions([PermissionRoles.GLOBAL_ADMIN]) && (
				<>
					<div className="flex flex-row mt-5 border-t pt-5 border-gray-200 text-gray-700 items-center text-sm">
						<span className="mr-1">1x</span>
						<Icon className="w-[16px] h-[16px] text-gray-700 mr-1" icon={faCoin} />
						<span className="">per row</span>
					</div>
					{applyTo && currentCreditCost > 0 && (
						<div className="flex flex-row pt-5 text-gray-700 items-center text-sm">
							<span className="mr-1">{currentCreditCost}</span>
							<Icon className="w-[16px] h-[16px] text-gray-700 mr-1" icon={faCoin} />
							<span className="">total</span>
						</div>
					)}
				</>
			)}
			{getAllErrors(error, formErrors).length ? (
				<div className="mt-4 mb-0">{renderErrorMessages(getAllErrors(error, formErrors))}</div>
			) : null}
		</Modal>
	);
};
